import { MaterialReactTable } from 'material-react-table'

import { getDateString } from '../../../utils/date-helpers'
import { useOneShopBroadcasts } from '../oneshop-broadcast-utils'
import { Card, CardContent } from '../../../components/ui/card'

import type { MRT_ColumnDef } from 'material-react-table'

import type { OneShopBroadcast } from '../oneshop-broadcast.schema'

const columns: Array<MRT_ColumnDef<OneShopBroadcast>> = [
  {
    accessorKey: 'date.iso',
    header: 'Date',
    Cell: ({ row }) => {
      return <p>{getDateString(row.original)}</p>
    },
  },
  { accessorKey: 'status', header: 'Status' },
  {
    id: 'messages',
    header: 'Messages',
    Cell: ({ row }) => {
      const smsCount = row.original.smsCount || 0
      const emailCount = row.original.emailCount || 0
      const total = smsCount + emailCount

      return <p>{total}</p>
    },
  },
  {
    id: 'completion',
    header: '% Complete',
    Cell: ({ row }) => {
      const smsCount = row.original.smsCount || 0
      const emailCount = row.original.emailCount || 0
      const smsCountSent = row.original.smsCountSent || 0
      const emailCountSent = row.original.emailCountSent || 0
      const total = smsCount + emailCount
      const sent = smsCountSent + emailCountSent

      return <p>{total ? Math.round((sent / total) * 100) : 100}%</p>
    },
  },
  {
    id: 'delay total',
    header: 'Delay Total(s)',
    Cell: ({ row }) => {
      const date = row.original.date.iso
      const lastMessageSentAt = row.original.lastMessageSentAt?.iso
      if (!date || !lastMessageSentAt) return <> </>

      const diffInMs =
        new Date(lastMessageSentAt).getTime() - new Date(date).getTime()

      // Convert milliseconds to seconds
      const diffInSeconds = Math.floor(diffInMs / 1000)
      return <p>{diffInSeconds}</p>
    },
  },
  {
    id: 'delay queue',
    header: 'Delay Queue(s)',
    Cell: ({ row }) => {
      const date = row.original.date.iso
      const queuedAt = row.original.queuedAt?.iso
      if (!queuedAt || !date) return <> </>

      const diffInMs = new Date(queuedAt).getTime() - new Date(date).getTime()

      // Convert milliseconds to seconds
      const diffInSeconds = Math.floor(diffInMs / 1000)
      return <p>{diffInSeconds}</p>
    },
  },
  {
    id: 'delay sending',
    header: 'Delay Sending(s)',
    Cell: ({ row }) => {
      const queuedAt = row.original.queuedAt?.iso
      const sentAt = row.original.sentAt?.iso
      if (!queuedAt || !sentAt) return <> </>

      const diffInMs = new Date(sentAt).getTime() - new Date(queuedAt).getTime()

      // Convert milliseconds to seconds
      const diffInSeconds = Math.floor(diffInMs / 1000)
      return <p>{diffInSeconds}</p>
    },
  },
  {
    id: 'delay sent',
    header: 'Delay Sent(s)',
    Cell: ({ row }) => {
      const sentAt = row.original.sentAt?.iso
      const lastMessageSentAt = row.original.lastMessageSentAt?.iso
      if (!sentAt || !lastMessageSentAt) return <> </>

      const diffInMs =
        new Date(lastMessageSentAt).getTime() - new Date(sentAt).getTime()

      // Convert milliseconds to seconds
      const diffInSeconds = Math.floor(diffInMs / 1000)
      return <p>{diffInSeconds}</p>
    },
  },
  { accessorKey: 'errors', header: 'Errors' },
  { accessorKey: 'title', header: 'Title' },
  { accessorKey: 'account.companyName', header: 'Company Name' },
  { accessorKey: 'account.objectId', header: 'Account ID' },
  { accessorKey: 'objectId', header: 'Broadcast ID' },
  { accessorKey: 'processCount', header: 'Process Count' },
]

function OneShopBroadcastTable() {
  const {
    data,
    isError,
    isPending,
    isPreviousData,
    totalCount,
    pagination,
    setPagination,
  } = useOneShopBroadcasts()

  if (isError) {
    return (
      <Card>
        <CardContent>Something went wrong loading the broadcasts.</CardContent>
      </Card>
    )
  }

  return (
    <div className="ion-padding">
      <Card>
        <CardContent>
          <MaterialReactTable
            columns={columns}
            data={data ?? []}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableColumnOrdering
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableGlobalFilter={false}
            enableHiding={false}
            enableRowActions={false}
            enableSorting={false}
            enableToolbarInternalActions={false}
            enableTopToolbar={false}
            initialState={{
              showGlobalFilter: false,
              showColumnFilters: false,
              density: 'compact',
            }}
            manualPagination
            manualFiltering
            onPaginationChange={setPagination}
            rowCount={totalCount}
            renderEmptyRowsFallback={() => (
              <div className="flex flex-col items-center justify-center p-20">
                <p>No broadcasts</p>
              </div>
            )}
            state={{
              pagination,
              isLoading: isPending,
              showProgressBars: isPending || isPreviousData,
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export { OneShopBroadcastTable }
